'use client'

/**
 * A sign-in button that leaves for a provider (branded sign-in, 3 Nov 2026; screens A1 to A5): the company's own
 * Microsoft, Google, SAML or OpenID Connect app ("CSB Microsoft"), or HumanFirewall's shared Microsoft or Google.
 *
 * HumanFirewall is a security-awareness platform. The words follow the screens: in a row beside the form the
 * provider's name alone ("Microsoft" on CSB's page is CSB's app, A3), as the only way in "Sign in with CSB Microsoft"
 * (A4), on dash once the e-mail named the company "Continue with CSB Microsoft" (A1, A2). Today's button shape: outline,
 * h-11 on a phone and h-10 above, the provider's mark at the start; the one primary way in is solid (the
 * action-button hierarchy: never two solid primaries).
 */

import type { ReactNode } from 'react'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useTranslation } from '@/lib/i18n'
import { cn } from '@/lib/utils'
import { SignInProviderIcon } from './sign-in-provider-icon'

export interface SignInRedirectButtonModel {
  method: string
  provider: 'microsoft' | 'google' | 'saml' | 'oidc'
  /** The company's name for its app, or null for HumanFirewall's shared app. */
  label: string | null
  /** What the button says in a row beside the form; null = the provider's name. */
  shortLabel: string | null
}

export type SignInRedirectWording = 'short' | 'signInWith' | 'continueWith'

export function SignInRedirectButton({
  button,
  wording,
  primary = false,
  loading = false,
  disabled = false,
  onClick,
  children,
}: {
  button: SignInRedirectButtonModel
  wording: SignInRedirectWording
  primary?: boolean
  loading?: boolean
  disabled?: boolean
  onClick: () => void
  /** Drawn after the words (the "Last used" badge). */
  children?: ReactNode
}) {
  const { t } = useTranslation()
  const provider =
    button.provider === 'microsoft'
      ? t('auth.microsoft')
      : button.provider === 'google'
        ? t('auth.google')
        : t('signInPage.providers.sso')
  const name = button.label ?? provider
  const words =
    wording === 'short'
      ? (button.shortLabel ?? provider)
      : wording === 'signInWith'
        ? t('signInPage.buttons.signInWith', { app: name })
        : t('signInPage.buttons.continueWith', { app: name })
  return (
    <Button
      type="button"
      variant={primary ? 'default' : 'outline'}
      className={cn('h-11 w-full sm:h-10', primary && wording !== 'short' && 'sm:h-11')}
      disabled={disabled}
      data-method={button.method}
      onClick={onClick}
    >
      {loading ? (
        <Loader2 className="me-2 h-4 w-4 animate-spin" aria-hidden="true" />
      ) : (
        <SignInProviderIcon provider={button.provider} className="me-2" />
      )}
      {words}
      {children}
    </Button>
  )
}
